// Labels and formatting shared by the campaign list and the campaign page.
//
// Two things here are load-bearing.
//
// **Every timestamp from this API is naive UTC.** The backend stores and
// returns `2026-09-15T14:00:00` with no zone, because MySQL DATETIME has
// nowhere to put one. `new Date("2026-09-15T14:00:00")` parses that as LOCAL
// time, so a screen playing until 14:00 UTC would read as 14:00 wherever the
// viewer happens to be -- silently wrong by hours, and worst for exactly the
// advertisers who buy screens in another timezone. utc() appends the Z.
//
// **The words are the API's honesty, translated.** `in_progress` and `live`
// are different states on purpose: the first means the campaign's dates cover
// today, the second means a screen is actually showing the ad right now. The
// labels below keep that distinction rather than calling both "Live".

export function utc(iso) {
    if (!iso) return null;
    // Already zoned (some fields come from isoformat() with an offset).
    const zoned = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
    const d = new Date(zoned);
    return isNaN(d.getTime()) ? null : d;
}

export function dayMonth(iso) {
    const d = utc(iso);
    return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
}

export function dateTime(iso) {
    const d = utc(iso);
    return d ? d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    }) : "—";
}

export function timeOnly(iso) {
    const d = utc(iso);
    return d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—";
}

export function money(cents) {
    if (cents == null) return "—";
    return "$" + (cents / 100).toFixed(2);
}

export const STATUS = {
    live: { label: "Live", tone: "ok" },
    in_progress: { label: "Running, nothing on screen", tone: "warn" },
    scheduled: { label: "Scheduled", tone: "info" },
    awaiting_approval: { label: "Awaiting hosts", tone: "warn" },
    ended: { label: "Ended", tone: "" },
    cancelled: { label: "Cancelled", tone: "" },
    payment_failed: { label: "Payment failed", tone: "danger" },
};

// Per-screen state, from utils/liveness.py. The tone ordering matters as much
// as the words: a host pausing a screen is amber (someone chose it), a screen
// nobody can reach is red (nobody knows what is on it).
export const SCREEN_STATE = {
    playing: { label: "Playing", tone: "ok", dot: "ok" },
    unreachable: { label: "Unreachable", tone: "danger", dot: "danger" },
    failed_to_play: { label: "Failed to play", tone: "danger", dot: "danger" },
    paused_by_host: { label: "Paused by host", tone: "warn", dot: "warn" },
    creative_disabled: { label: "Ad held for review", tone: "warn", dot: "warn" },
    off_air: { label: "Off air", tone: "", dot: "idle" },
    upcoming: { label: "Scheduled", tone: "info", dot: "idle" },
    awaiting_approval: { label: "Awaiting host", tone: "warn", dot: "warn" },
    expired: { label: "Never approved", tone: "", dot: "idle" },
    ended: { label: "Finished", tone: "", dot: "idle" },
    taken_down: { label: "Taken down by host", tone: "danger", dot: "danger" },
    cancelled: { label: "Cancelled", tone: "", dot: "idle" },
    declined: { label: "Declined", tone: "", dot: "idle" },
};

export function screenState(state) {
    return SCREEN_STATE[state] || { label: state || "Unknown", tone: "", dot: "idle" };
}

// The one-line explanation under a screen's state. Only the states that have
// something concrete to add say anything; the rest stay quiet rather than
// padding every row with a restatement of its own label.
export function stateDetail(now) {
    if (!now) return null;
    switch (now.state) {
        case "playing":
            return now.until ? `Until ${dateTime(now.until)}` : null;
        case "upcoming":
            return now.starts_at ? `Starts ${dateTime(now.starts_at)}` : null;
        case "off_air":
            return now.next_on_air_at
                ? `Back on air ${dateTime(now.next_on_air_at)}`
                : "No on-air hours left in this booking";
        case "unreachable":
            return now.minutes_since_seen != null
                ? `No check-in for ${now.minutes_since_seen} min`
                : "This screen has never checked in";
        case "failed_to_play":
            return now.failed_at ? `Last failure ${dateTime(now.failed_at)}` : null;
        case "creative_disabled":
            return "Your ad is under review, so the screen shows its placeholder";
        case "paused_by_host":
            return "The host has stopped this screen";
        default:
            return null;
    }
}

export const FIT_TONE = { poor: "danger", letterboxed: "warn", good: "ok", unknown: "" };
